import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, MoveHorizontalIcon } from 'lucide-react';
import { materialSlides } from '../data/content';
const AUTOPLAY_MS = 6000;
export function MaterialCarousel() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const total = materialSlides.length;
  const slide = materialSlides[index];
  const goTo = useCallback((next: number, manual = true) => {
    setDirection(next > index || index === total - 1 && next >= total ? 1 : -1);
    setIndex((next + total) % total);
    if (manual) setIsPaused(true);
  }, [index, total]);
  useEffect(() => {
    if (isPaused) return;
    const id = window.setTimeout(() => goTo(index + 1, false), AUTOPLAY_MS);
    return () => window.clearTimeout(id);
  }, [index, isPaused, goTo]);
  return <section id="materiale" className="w-full overflow-hidden bg-navy-dark px-3 py-20 sm:px-5 sm:py-28" data-id="element-145" data-mp="2mmnby5t4qqrm">
      <div className="mx-auto max-w-5xl" data-id="element-146" data-mp="2hobzrofd4468">
        <div className="text-center" data-id="element-147" data-mp="2hpdmiq5qqf3s">
          <span className="inline-flex items-center gap-2 rounded-full bg-tangerine px-4 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.2em] text-white shadow-cta" data-id="element-148" data-mp="2vep9nwbtdsy7">
            Sbircia dentro il pack
          </span>
          <h2 className="mt-5 font-display text-[1.9rem] font-black leading-tight tracking-tight text-white sm:text-[2.8rem]" data-id="element-149" data-mp="35c2b1up995td">
            Guarda il materiale{' '}
            <span className="italic text-tangerine-light" data-id="element-150" data-mp="3edthx2vj7iot">
              dall&apos;interno
            </span>
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-sm leading-relaxed text-white/65 sm:text-base" data-id="element-151" data-mp="3abydvqm7ur7c">
            Pagine reali dei volumi: teoria visuale, attività e schede già
            impaginate, pronte da stampare o da proiettare sulla LIM.
          </p>
        </div>

        <div className="mt-12 grid items-center gap-10 md:grid-cols-[minmax(0,1fr)_280px]" data-id="element-152" data-mp="3f2ynmk30ypz6">
          <div className="relative mx-auto flex w-full max-w-[700px] items-center gap-0 sm:gap-5" data-id="element-153" data-mp="3k21rw2b6ks63">
            <button type="button" onClick={() => goTo(index - 1)} aria-label="Pagina precedente" className="absolute left-1 top-1/2 z-10 flex h-10 w-10 shrink-0 -translate-y-1/2 items-center justify-center rounded-full bg-navy/75 text-white backdrop-blur transition-colors duration-200 hover:bg-tangerine focus:outline-none focus-visible:ring-2 focus-visible:ring-tangerine sm:static sm:h-11 sm:w-11 sm:translate-y-0 sm:bg-tangerine sm:hover:bg-tangerine-dark" data-id="element-154" data-mp="3p14mestjbfzt">
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-155" data-mp="glky887q0nrm" />
            </button>

            <div className="relative flex-1" data-id="element-156" data-mp="lkxrpbt3kt36">
              <div className="relative aspect-[3/4] overflow-hidden rounded-[28px] border-4 border-tangerine/70 bg-white shadow-[0_40px_70px_-35px_rgba(0,0,0,0.75)]" data-id="element-157" data-mp="lm8xfh63sawx">
                <AnimatePresence initial={false} mode="wait" data-id="element-158" data-mp="2a53b05kseqh2">
                  <motion.img key={slide.src} src={slide.src} alt={`Pagina del materiale: ${slide.title}`} drag="x" dragConstraints={{
                  left: 0,
                  right: 0
                }} dragElastic={0.12} onDragEnd={(_, info) => {
                  if (info.offset.x < -60) goTo(index + 1);
                  if (info.offset.x > 60) goTo(index - 1);
                }} initial={{
                  opacity: 0,
                  x: direction * 48
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: direction * -48
                }} transition={{
                  duration: 0.28,
                  ease: [0.23, 1, 0.32, 1]
                }} className="absolute inset-0 h-full w-full cursor-grab object-contain p-1.5 active:cursor-grabbing sm:p-2" data-id="element-159" data-mp="262gjy6vxx9kx" />
                </AnimatePresence>
              </div>

              <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-tangerine px-4 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.14em] text-white shadow-cta" data-id="element-160" data-mp="2s8zpwsvfm06m">
                {slide.label}
              </span>

              <div className="mt-5 h-1 w-full overflow-hidden rounded-full bg-white/15" data-id="element-161" data-mp="2nag0gihmq69i">
                <motion.div key={`${index}-${isPaused}`} className="h-full rounded-full bg-tangerine" initial={{
                width: isPaused ? '100%' : '0%'
              }} animate={{
                width: '100%'
              }} transition={{
                duration: isPaused ? 0 : AUTOPLAY_MS / 1000,
                ease: 'linear'
              }} data-id="element-162" data-mp="3b0acixu4jqsp" />
              </div>
            </div>

            <button type="button" onClick={() => goTo(index + 1)} aria-label="Pagina successiva" className="absolute right-1 top-1/2 z-10 flex h-10 w-10 shrink-0 -translate-y-1/2 items-center justify-center rounded-full bg-navy/75 text-white backdrop-blur transition-colors duration-200 hover:bg-tangerine focus:outline-none focus-visible:ring-2 focus-visible:ring-tangerine sm:static sm:h-11 sm:w-11 sm:translate-y-0 sm:bg-tangerine sm:hover:bg-tangerine-dark" data-id="element-163" data-mp="3txjzgoynrmep">
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-164" data-mp="1s3ypahawlgn6" />
            </button>
          </div>

          <div className="text-center md:text-left" aria-live="polite" data-id="element-165" data-mp="zcx68543b0yd">
            <p className="font-display text-sm font-bold tabular-nums text-tangerine-light" data-id="element-166" data-mp="zf041bbhhydn">
              {String(index + 1).padStart(2, '0')}
              <span className="text-white/35" data-id="element-167" data-mp="18d3p66ik4t4x"> / {String(total).padStart(2, '0')}</span>
            </p>
            <h3 className="mt-3 font-display text-xl font-black leading-snug text-white sm:text-2xl" data-id="element-168" data-mp="2or299e13j2tz">
              {slide.title}
            </h3>
            <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-white/60 md:mx-0" data-id="element-169" data-mp="2jsihuy3qgyu8">
              {slide.text}
            </p>

            <ul className="mt-7 flex items-center justify-center gap-3 md:justify-start" data-id="element-170" data-mp="1mdxnshpxf9ot">
              {materialSlides.map((item, itemIndex) => <li key={item.src} data-id="element-171" data-mp="1qgtyznd503iq" data-mpi={itemIndex}>
                  <button type="button" onClick={() => goTo(itemIndex)} aria-label={`Vai alla pagina ${itemIndex + 1}: ${item.title}`} aria-current={itemIndex === index} className={`block overflow-hidden rounded-xl border-2 bg-white transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-tangerine ${itemIndex === index ? 'border-tangerine' : 'border-white/15 hover:border-white/45'}`} data-id="element-172" data-mp="1vf5by6yqau56">
                    <img src={item.src} alt="" className={`h-24 w-16 object-cover object-top transition-opacity duration-200 ${itemIndex === index ? 'opacity-100' : 'opacity-55'}`} data-id="element-173" data-mp="jyx4br4fwsmn" />
                  </button>
                </li>)}
            </ul>

            <p className="mt-6 flex items-center justify-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/40 md:justify-start" data-id="element-174" data-mp="3ul0txha2fx8z">
              <MoveHorizontalIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-175" data-mp="3kw96nmfya14e" />
              Trascina per sfogliare
            </p>
          </div>
        </div>
      </div>
    </section>;
}